/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';
import { DeleteOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';

import fieldTypes from './formFields.types';
import BorderlessButton from '../../../common/buttons/BorderlessButton';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';
import colors from '../../../constants/Colors';
import { fieldTypesWithAPI, isInvalidMultiDivisionField } from '../../formHelpers';
import { dataTypesAllowedInExternalForm, MULTI_DIVISION_INVALID_FIELD_MESSAGE } from '../../forms.constants';
import {
  getVendors,
  getProjects,
  getUsers,
  getEquipment,
  getPhases,
  getCustomerList,
  getVendorList,
  getProjectIdMap,
  getPhaseIdMap,
  getUserIdMap,
  getEquipmentIdMap,
} from '../../../state/selectors';
import useUpdateCostcodeIdMap from '../../../costcodes/useUpdateCostcodeIdMap';
import { ENTRY_COSTCODE_COLUMNS } from '../../hooks/useEntryCostcodes';

const buttonStyle = {
  pointerEvents: 'auto',
  width: 30,
  padding: 0,
};

const textFields = new Set(['text', 'calculation', 'attribute', 'staticText']);

export default function FormField({
  projectId,
  sectionId,
  index,
  onDelete,
  onEdit,
  field = {},
  isDisplay = false,
  draggable = true,
  isExternalForm = false,
  isMultiDivisionForm = false,
  divisions,
  templateId,
  name,
  showCondensedView = false,
  isDetailView = false,
  costcodes,
  costcodeIdMap: entryCostcodeIdMap,
}) {
  const { t } = useTranslation();
  // Use reselect memoized selectors - these are globally cached across all components
  const projects = useSelector(getProjects);
  const users = useSelector(getUsers);
  const equipment = useSelector(getEquipment);
  const phases = useSelector(getPhases);
  const vendors = useSelector(getVendors);

  // Memoized transformations - computed once globally via reselect
  const customerList = useSelector(getCustomerList);
  const vendorList = useSelector(getVendorList);
  const projectIdMap = useSelector(getProjectIdMap);
  const phaseIdMap = useSelector(getPhaseIdMap);
  const userIdMap = useSelector(getUserIdMap);
  const equipmentIdMap = useSelector(getEquipmentIdMap);

  // These don't need selectors yet (not commonly transformed)
  const materials = useSelector((state) => state.materials.materials);
  const customTables = useSelector((state) => state.forms.customTables);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const positions = useSelector((state) => state.settings.positions);
  const bucketTemplates = useSelector((state) => state.buckets.bucketTemplates);
  const buckets = useSelector((state) => state.buckets.buckets);
  const statuses = useSelector((state) => state.statuses.statuses);
  const divisionMap = useSelector((state) => state.settings.divisions);
  const stateCostcodeIdMap = useSelector((state) => state.costcodes.costcodeIdMap);
  const {
    selectedType,
    configProps,
    id:
    fieldId,
    response,
  } = field;

  const { width: fieldWidth = '100' } = configProps || {};
  // Use calc to account for gap between half-width fields
  const widthStyle = fieldWidth === '50' ? 'calc(50% - 5px)' : '100%';

  const fieldObj = fieldTypes[selectedType];

  const {
    values: [{
      divisionId,
    } = {}] = [],
  } = response || {};

  // HARBOUR-4222: Used to find a division for Board Card Time Entry
  // Table Display Field since there is none passed from boards
  const parsedDivisions = useMemo(() => {
    if (divisions?.length) {
      return divisions;
    }

    return divisionId ? [divisionId] : [];
  }, [divisions, divisionId]);

  const divisionMaterials = useMemo(() => {
    if (!isMultiDivisionForm) return materials;

    const divisionSet = new Set(parsedDivisions);
    return Object.values(materials).reduce((acc, mat) => {
      if (divisionSet.has(mat.divisionId)) {
        acc[mat.id] = mat;
      }
      return acc;
    }, {});
  }, [materials, parsedDivisions, isMultiDivisionForm]);

  const responseValues = useMemo(() => response?.values ?? [], [response]);

  useUpdateCostcodeIdMap({ items: responseValues, columns: ENTRY_COSTCODE_COLUMNS });

  const costcodeIdMap = useMemo(() => ({
    ...stateCostcodeIdMap,
    ...entryCostcodeIdMap,
  }), [stateCostcodeIdMap, entryCostcodeIdMap]);

  const isInvalidField = (
    (isExternalForm
      && (
        (fieldTypesWithAPI.has(selectedType) && configProps?.dataType !== 'Custom')
        || (
          selectedType === 'dropdown'
          && !dataTypesAllowedInExternalForm.has(configProps?.dataType)
        )
      ))
    || (isMultiDivisionForm && isInvalidMultiDivisionField(field))
  );

  const invalidFieldMessage = isMultiDivisionForm
    ? MULTI_DIVISION_INVALID_FIELD_MESSAGE
    : 'This field is not supported in external forms.';

  const isText = textFields.has(selectedType);
  const isGPS = selectedType === 'gpsLocation';
  const isDropdown = selectedType === 'dropdown';

  const form = useRef(null);

  const handleDelete = useCallback(() => {
    if (!onDelete) return;
    const fieldLabel = configProps?.title || 'this field';
    CustomConfirmModal({
      title: `Delete ${fieldLabel}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      onOk: () => {
        onDelete(fieldId)();
      },
    });
  }, [onDelete, fieldId, configProps]);

  if (!fieldObj) return null;
  const { preview } = fieldObj;
  if (!preview) return null;
  const draggableId = `${sectionId}-${fieldId}`;

  const body = (
    <div
      className={isDetailView && !isGPS ? 'form-field-display-container' : ''}
      style={{
        pointerEvents: isText || isGPS || isDropdown ? 'auto' : 'none',
        // Width is applied on wrapper (either FormFieldList wrapper or Draggable wrapper)
        // Container should always fill its wrapper
        width: '100%',
        display: isDetailView ? 'flex' : 'inline-block',
        flexDirection: isDetailView ? 'column' : undefined,
        height: isDetailView ? '100%' : undefined,
        verticalAlign: isDetailView ? undefined : 'top',
      }}
    >
      {preview({
        id: fieldId,
        projectId,
        configProps,
        previewProps: response || configProps?.presetData || {},
        disabled: true,
        isDisplay,
        formRef: { current: form },
        users,
        projects,
        materials: divisionMaterials,
        equipment,
        costcodes,
        phases,
        customers: customerList,
        vendors: vendorList,
        vendorIdMap: vendors,
        customTables,
        projectIdMap,
        equipmentIdMap,
        costcodeIdMap,
        phaseIdMap,
        userIdMap,
        divisions: parsedDivisions,
        divisionMap,
        templateId,
        name,
        showCondensedView,
        settings,
        t,
        isFormBuilder: !isDetailView,
        scheduleCategories,
        positions,
        bucketTemplates,
        buckets,
        statuses,
      })}
    </div>
  );

  if (!draggable) return body;
  return (
    <Draggable
      key={draggableId}
      draggableId={draggableId}
      index={index}
      type="FIELD"
      isDragDisabled={isDisplay}
    >
      {({ draggableProps, dragHandleProps, innerRef }) => (
        <div
          {...draggableProps}
          {...dragHandleProps}
          className={isDetailView ? '' : 'form-field-preview'}
          style={{
            margin: '10px 0px',
            position: 'relative',
            width: widthStyle,
            flexBasis: fieldWidth === '100' ? '100%' : widthStyle,
            flexGrow: 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            ...draggableProps.style,
            border: isInvalidField ? '1px solid red' : 'none',
          }}
          key={index}
          ref={innerRef}
        >
          {body}
          {!isDisplay && (
            <BorderlessButton
              style={{
                position: 'absolute',
                right: 40,
                top: 5,
                ...buttonStyle,
              }}
              iconNode={<EditOutlined style={{ marginLeft: 0 }} />}
              onClick={onEdit(field)}
            />
          )}
          {!isDisplay && (
            <BorderlessButton
              style={{
                position: 'absolute',
                right: 10,
                top: 5,
                ...buttonStyle,
              }}
              iconNode={<DeleteOutlined style={{ color: colors.ONTRACCR_RED, marginLeft: 0 }} />}
              onClick={handleDelete}
            />
          )}
          { !!isInvalidField && (
            <Popover
              placement="bottomLeft"
              trigger="hover"
              content={invalidFieldMessage}
              title="Warning"
            >
              <WarningOutlined
                style={{
                  color: colors.ONTRACCR_DARK_YELLOW,
                  position: 'absolute',
                  top: 12,
                  right: 77,
                }}
              />
            </Popover>
          )}
        </div>
      )}
    </Draggable>
  );
}

FormField.propTypes = {
  projectId: PropTypes.string,
  sectionId: PropTypes.string,
  index: PropTypes.number,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  field: PropTypes.shape({
    id: PropTypes.string,
    selectedType: PropTypes.string,
    configProps: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })),
    }),
    response: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),
  }),
  isDisplay: PropTypes.bool,
  draggable: PropTypes.bool,
  isExternalForm: PropTypes.bool,
  divisions: PropTypes.arrayOf(PropTypes.string),
  templateId: PropTypes.string,
  name: PropTypes.string,
  showCondensedView: PropTypes.bool,
  isDetailView: PropTypes.bool,
  isMultiDivisionForm: PropTypes.bool,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeIdMap: PropTypes.shape({}),
};

FormField.defaultProps = {
  projectId: null,
  sectionId: null,
  index: null,
  onDelete: null,
  onEdit: null,
  field: {},
  isDisplay: false,
  draggable: true,
  isExternalForm: false,
  costcodes: [],
  costcodeIdMap: {},
  divisions: [],
  templateId: null,
  name: null,
  showCondensedView: false,
  isDetailView: false,
  isMultiDivisionForm: false,
};
